import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import {
  ModificationRequestIssueID,
  ModificationRequestItemId,
  ModificationRequestState,
} from '@kaizenplatform/hugot-entity';
import { Button, FormControl, FormHelperText, TextField } from '@material-ui/core';
import { createElement, FC } from 'react';
import { FormattedMessage } from 'react-intl';

import CreateIssue from '../../../../entity/CreateIssue';
import UploadingFile from '../../../../entity/UploadingFile';
import UploadingFilesProgress from '../../../../entity/UploadingFilesProgress';
import useInputComment, { inputCommentLimit } from '../../../../hooks/useInputComment';
import { SPACING_REGULAR } from '../../../constant/spacing';
import FileList, { FileListItem, FileListUploadingItem } from '../FileList';
import _FileUploader from '../FileUploader';

interface Props {
  itemId: ModificationRequestItemId;
  modificationRequestState: ModificationRequestState;
  bucketId: string | null;
  files: Array<FileReference>;
  createIssue: CreateIssue;
  onRemoveAssetIssueFile: (bucketId: string, fileId: string, issueId: ModificationRequestItemId) => void;
  onUploadIssueAssetFiles: (files: UploadingFile[], bucketId: string, itemId: ModificationRequestItemId) => void;
  onCancel: () => void;
  onSubmit: (
    itemId: ModificationRequestIssueID,
    inputComment: string,
    bucketId: string | null,
    files: Array<FileReference>,
  ) => Promise<void>;
  className?: string;
}

const CreateIssueDetailForm: FC<Props> = ({
  itemId,
  bucketId,
  files,
  createIssue,
  onRemoveAssetIssueFile,
  onUploadIssueAssetFiles,
  onCancel,
  onSubmit,
  className,
}) => {
  const [inputComment, commentHandler] = useInputComment('');

  const {
    assetIds,
    uploadingAssetFiles,
    uploadingFilesProgress,
  }: {
    assetIds: Array<string>;
    uploadingAssetFiles: UploadingFile[];
    uploadingFilesProgress: UploadingFilesProgress;
  } = createIssue;

  const uploadedFiles = files.filter((file) => assetIds.includes(file.id));

  return (
    <Root className={className} id={'issue.id'}>
      <FormArea>
        <CommentForm error={inputComment.error}>
          <Comment
            label={<FormattedMessage id="modification_request.detail_card.input_comment.label" />}
            value={inputComment.body}
            variant="outlined"
            rows="4"
            rowsMax="8"
            multiline
            error={inputComment.error}
            onChange={(e) => commentHandler.setComment(e.target.value)}
          />
          <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
        </CommentForm>

        {(bucketId && uploadedFiles.length > 0) || uploadingAssetFiles.length > 0 ? (
          <FileList>
            {[
              ...uploadedFiles.map((file) => (
                <FileListItem
                  file={file}
                  key={file.id}
                  isVisibleSecondary={false}
                  onClickDeleteButton={() => onRemoveAssetIssueFile(bucketId!, file.id, itemId)}
                />
              )),
              ...uploadingAssetFiles.map((file) => {
                const targetProgressFile = uploadingFilesProgress[file.id];

                return (
                  <FileListUploadingItem
                    file={file}
                    key={`uploading-${file.id}`}
                    progress={targetProgressFile ? targetProgressFile.progress : 0}
                    state="uploading"
                  />
                );
              }),
            ]}
          </FileList>
        ) : null}

        {bucketId ? (
          <FileUploader
            onSelectFiles={(_, files) => {
              onUploadIssueAssetFiles(files, bucketId, itemId);
            }}
          />
        ) : null}

        <Buttons>
          <CancelButton color="default" variant="contained" size="small" onClick={onCancel}>
            <FormattedMessage id="modification_request_creating_card.cancel" />
          </CancelButton>
          <Submit
            variant="contained"
            color="primary"
            size="small"
            disabled={inputComment.body.trim() === '' || inputComment.error || uploadingAssetFiles.length > 0}
            onClick={() => {
              commentHandler.setComment('');
              onSubmit(itemId, inputComment.body, bucketId, uploadedFiles);
            }}
          >
            <FormattedMessage id="modification_request.detail_card.issue.submit" />
          </Submit>
        </Buttons>
      </FormArea>
    </Root>
  );
};

const Root = styled('div')({});

const FormArea = styled('div')({
  width: '100%',
});

const CommentForm = styled(FormControl)({
  width: '100%',
  marginBottom: SPACING_REGULAR,
});

const Comment = styled(TextField)({
  width: '100%',
});

const InputCount = styled(FormHelperText)({
  textAlign: 'right',
});

const FileUploader = styled(_FileUploader)({
  padding: SPACING_REGULAR,
});

const Buttons = styled('div')({
  textAlign: 'right',
});
const CancelButton = styled(Button)({
  marginTop: SPACING_REGULAR,
  marginRight: SPACING_REGULAR,
  width: 'auto',
});
const Submit = styled(Button)({
  marginTop: SPACING_REGULAR,
  width: 'auto',
});

export default CreateIssueDetailForm;
